import React, {useCallback, useState} from 'react';
import {Edge, Node} from 'reactflow';
import {isEqual, isUndefined, omitBy} from 'lodash';
import {v4 as uuidv4} from 'uuid';
import {AutomationNode, NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {getDefaultNodeConfig, getNodeConfig} from '../config/nodeCategories';
import {reconnectAroundNode, stepLabel, uniqueStepLabel} from '../utils/flowGraph';

// `undefined` values are no difference: JSON drops them (the Audience step retires `filter`/`query` that way).
const isSameConfig = (left: Record<string, any>, right: Record<string, any>) => isEqual(
    omitBy(left, isUndefined),
    omitBy(right, isUndefined)
);

export const useNodeOperations = (onDirty?: () => void) => {
    const [editingNode, setEditingNode] = useState<AutomationNode | null>(null);
    const [nodeConfig, setNodeConfig] = useState<Record<string, any>>({});
    const [nodeLabel, setNodeLabel] = useState('');
    const [configDialogOpen, setConfigDialogOpen] = useState(false);

    const openNodeEditor = useCallback((nodeData: AutomationNode) => {
        setEditingNode(nodeData);
        setNodeConfig(nodeData.config || {});
        setNodeLabel(nodeData.label ?? nodeData.config?.label ?? '');
        setConfigDialogOpen(true);
    }, []);

    // `configOverride` comes from a dialog's before-save hook (Audience's new audience id): a `setNodeConfig`
    // write would reach this callback too late. Returns `nodes` itself when nothing changed: no edit, no undo step.
    const getConfiguredNodes = useCallback((
        nodes: Node[],
        configOverride?: Record<string, any>
    ) => {
        if (!editingNode?.id) return nodes;

        const target = nodes.find(node => node.id === editingNode.id);
        const configToSave = configOverride ?? nodeConfig;
        const {label: _legacyConfigLabel, ...configWithoutLabel} = configToSave;

        const fallbackLabel = (target && (getNodeConfig(target.data.type)?.label || target.data.type))
            || '';
        const label = nodeLabel.trim() || fallbackLabel;

        // Compare labels as the card shows them: writing the catalogue label onto an unlabelled node is no rename.
        const changed = !target
            || label !== (target.data.label || fallbackLabel)
            || !isSameConfig(configWithoutLabel, target.data.config ?? {});

        if (!changed) return nodes;

        return nodes.map(node => (node.id === editingNode.id
            ? {
                ...node,
                data: {
                    ...node.data,
                    config: configWithoutLabel,
                    label
                }
            }
            : node));
    }, [editingNode, nodeConfig, nodeLabel]);

    const handleSaveNodeConfig = useCallback((
        nodes: Node[],
        setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
        configOverride?: Record<string, any>,
        beforeChange?: () => void
    ) => {
        if (!editingNode?.id) return;

        const configuredNodes = getConfiguredNodes(nodes, configOverride);
        if (configuredNodes !== nodes) {
            beforeChange?.();
            setNodes(configuredNodes);
            onDirty?.();
        }

        setConfigDialogOpen(false);
        setEditingNode(null);
        setNodeConfig({});
        setNodeLabel('');
    }, [editingNode, getConfiguredNodes, onDirty]);

    const handleDeleteNode = useCallback((
        setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
        setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
    ) => {
        if (editingNode && editingNode.id) {
            const nodeId = editingNode.id;
            setNodes((nds) => nds.filter((node) => node.id !== nodeId));
            setEdges((eds) => reconnectAroundNode(eds, nodeId));
            setConfigDialogOpen(false);
            setEditingNode(null);
            setNodeConfig({});
            setNodeLabel('');
            onDirty?.();
        }
    }, [editingNode, onDirty]);

    const addNode = useCallback((
        nodeType: NodeType,
        label: string,
        position: { x: number; y: number },
        setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
        onNodeAdded?: (nodeId: string) => void
    ) => {
        const newNodeId = uuidv4();
        setNodes((nds) => [
            ...nds.map(node => node.selected ? {...node, selected: false} : node),
            {
                id: newNodeId,
                type: 'custom',
                position,
                selected: true,
                data: {
                    id: newNodeId,
                    type: nodeType,
                    label: uniqueStepLabel(label, nodeType, nds),
                    config: getDefaultNodeConfig(nodeType)
                }
            }
        ]);
        onNodeAdded?.(newNodeId);
    }, []);

    // `nodes`: the steps the copy joins, so its label is unique among them.
    const createNodeCopy = useCallback((
        source: Node,
        position: { x: number; y: number },
        nodes: Node[]
    ): Node => {
        const newNodeId = uuidv4();

        return {
            id: newNodeId,
            type: source.type,
            position,
            data: {
                ...source.data,
                id: newNodeId,
                label: uniqueStepLabel(stepLabel(source), source.data?.type, nodes),
                config: structuredClone(source.data?.config ?? {})
            }
        };
    }, []);

    return {
        editingNode,
        nodeConfig,
        setNodeConfig,
        nodeLabel,
        setNodeLabel,
        configDialogOpen,
        setConfigDialogOpen,
        openNodeEditor,
        handleSaveNodeConfig,
        handleDeleteNode,
        addNode,
        createNodeCopy
    };
};
